import React from "react";
import { Helmet } from "react-helmet-async";
import { useTheme } from "../../context/ThemeContext";
import { getThemeClasses } from "../utils/themeClass";

const AboutBrowserGames = () => {
  const { theme } = useTheme();
  const themeClass = getThemeClasses(theme);

  return (
    <div className={`${themeClass.bgColor} ${themeClass.baseText} min-h-screen`}>
    <div className={`max-w-4xl mx-auto px-4 py-10 ${themeClass.bgColor} ${themeClass.baseText} `}>
      <Helmet>
        <title>What Are Browser Games? | BrowserArena</title>
        <meta
          name="description"
          content="Play browser games instantly without downloads. Discover the best free action, puzzle, multiplayer, and strategy games online at BrowserArena."
        />
        <meta
          name="keywords"
          content="browser games, free online games, play games online, HTML5 games, multiplayer browser games"
        />
        <link rel="canonical" href="https://browserarena.com/about-browser-games" />
        <meta property="og:title" content="What Are Browser Games? | BrowserArena" />
        <meta property="og:description" content="Discover what makes browser games fun & accessible. Play instantly, no downloads, on BrowserArena." />
        <meta property="og:url" content="https://browserarena.com/about-browser-games" />
        <meta property="og:type" content="website" />
      </Helmet>

      <h1 className={`text-3xl sm:text-4xl font-extrabold mb-6 text-center ${themeClass.titleColor}`}>
        What Are Browser Games?
      </h1>

      <p className={`mb-6 text-base sm:text-lg ${themeClass.overlayText}`}>
        <strong>Browser games</strong> are online games playable directly in your web browser — no downloads, no installations, no hassle. They’re lightweight, accessible, and ready for instant fun on PC, laptop, tablet, or mobile.
      </p>

      <h2 className={`text-2xl font-bold mb-4 ${themeClass.titleColor}`}>How Do Browser Games Work?</h2>
      <p className={`mb-6 text-base sm:text-lg ${themeClass.overlayText}`}>
        Built with <strong>HTML5</strong>, <strong>WebGL</strong>, and <strong>JavaScript</strong>, modern browser games deliver immersive graphics and interactive experiences — all inside your browser window. Many now support 3D visuals, multiplayer modes, and seamless mobile play.
      </p>

      <h2 className={`text-2xl font-bold mb-4 ${themeClass.titleColor}`}>Why Play Browser Games?</h2>
      <ul className={`list-disc list-inside mb-6 text-base sm:text-lg space-y-2 ${themeClass.overlayText}`}>
        <li>No downloads or updates — click and play.</li>
        <li>Compatible with any device and OS.</li>
        <li>Huge variety: action, strategy, puzzles, shooters, and more.</li>
        <li>Great for short breaks or deep gaming sessions.</li>
        <li>Free to play — many with achievements and multiplayer.</li>
      </ul>

      <h2 className={`text-2xl font-bold mb-4 ${themeClass.titleColor}`}>Experience the Best at BrowserArena</h2>
      <p className={`mb-6 text-base sm:text-lg ${themeClass.overlayText}`}>
        <strong>BrowserArena</strong> curates the top free browser games online. Dive into <em>arcade, action, sports, puzzle</em>, and <em>multiplayer</em> hits — all optimized for desktop and mobile.
      </p>
      <p className={`mb-6 text-base sm:text-lg ${themeClass.overlayText}`}>
        Whether you want a quick break or a long gaming session, BrowserArena has you covered. Tap, play, and enjoy — no installs, no worries!
      </p>

      <h2 className={`text-2xl font-bold mb-4 ${themeClass.titleColor}`}>Popular Types of Browser Games</h2>
      <p className={`mb-6 text-base sm:text-lg ${themeClass.overlayText}`}>
        There’s a browser game for everyone! Explore our top categories:
      </p>

      <ul className={`list-disc list-inside mb-6 text-base sm:text-lg space-y-2 ${themeClass.overlayText}`}>
        <li><strong>Action Games:</strong> Fast-paced excitement and challenges.</li>
        <li><strong>Puzzle Games:</strong> Clever puzzles and brain teasers.</li>
        <li><strong>Multiplayer Games:</strong> Compete or co-op worldwide.</li>
        <li><strong>Sports Games:</strong> Score goals, race, and win big.</li>
        <li><strong>Strategy Games:</strong> Plan, build, and conquer smartly.</li>
        <li><strong>Arcade Games:</strong> Classic thrills and quick fun.</li>
      </ul>

      <div className="text-center mt-10">
        <a
          href="/"
          className={`inline-block px-6 py-3 font-bold rounded-full shadow-md hover:shadow-lg transition ${themeClass.exploreBtn}`}
        >
          🎮 Browse All Games
        </a>
      </div>
    </div>
    </div>
  );
};

export default AboutBrowserGames;
